import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc, GetMethodResponse, ListMethodArg, ListMethodResponse, ModelClass, References } from '/imports/types';
import { validateBoolean, validateString } from '/imports/validators';
import { strings } from '/imports/strings';
import { GmapsAddress } from '/imports/gmaps';

export interface ConsumptionMeter extends Doc {
  orgId: string;
  utilityAccountId?: string;
  name: string;
  accountNumber?: string;
  identifier: string;
  type?: 'sdge' | 'pge' | 'sce';
  address?: GmapsAddress;
  timezone?: string;
  expiresAt?: string;
  oneYearConsumption?: number;
}
export type Model = ConsumptionMeter;

export const ConsumptionMeters = new Mongo.Collection<ConsumptionMeter>('consumptionMeters');

type Refs = References<Model>

export const modelClass: ModelClass<Model, Refs> = {
  collection: ConsumptionMeters,
  editableFields: ['active', 'orgId', 'utilityAccountId', 'name', 'identifier', 'accountNumber', 'type', 'expiresAt', 'address', 'timezone',
  ],
  fieldLimits: {
    'accountNumber': 1024, 'expiresAt': 64, 'identifier': 1024, 'name': 256, 'orgId': 64, 'timezone': 64, 'type': 64,
    'utilityAccountId': 64,
  },
  isOrgOwned: true,
  listMethod: DeclareMethod<ListMethodArg<ConsumptionMeter>, ListMethodResponse<ConsumptionMeter, Refs>>('listConsumptionMeters'),
  getMethod: DeclareMethod<string, GetMethodResponse<ConsumptionMeter, Refs>>('getConsumptionMeter'),
  createMethod: DeclareMethod<Partial<ConsumptionMeter>, string>('createConsumptionMeter'),
  updateMethod: DeclareMethod<Partial<ConsumptionMeter>, string>('updateConsumptionMeter'),
  deleteMethod: DeclareMethod<string>('deleteConsumptionMeter'),
  pathPrefix: '/consumption-meters',
  asString: (document) => document.name || document.username || document._id,
  modelName: strings.ConsumptionMeter(),
  pluralName: strings.ConsumptionMeters(),
  searchQuery: (search?: string) => {
    search = search?.trim();
    if (!search?.length) return {};
    return {
      $or: [
        { _id: search },
        { name: new RegExp(search, 'gi') },
        { identifier: new RegExp(search, 'gi') },
        { type: search as ConsumptionMeter['type'] },
      ]
    };
  },
  validate: async (project: Partial<ConsumptionMeter>) => {
    const errors: { [key: string]: string[] } = {};
    validateBoolean(errors, project, 'active');
    validateString(errors, project, 'orgId', { required: true });
    validateString(errors, project, 'utilityAccountId');
    validateString(errors, project, 'name', { required: true });
    validateString(errors, project, 'identifier', { required: true });
    return errors;
  },
};

export const pullUtilityBills = DeclareMethod<{ meterIds: string[] }, void>('pullUtilityBills');
export const compileOneYearConsumption = DeclareMethod<undefined, { total: number, succeeded: number, skipped: number }>('compileOneYearConsumption');

/**
 * Map a meter's utility type code to its display label.
 * @param meter - meter whose type to format
 * @returns the display label, or empty string when no meter
 */
export const consumptionMeterType = (meter?: { type?: string }) => {
  if (!meter) return '';
  if (meter.type === 'sdge') return 'SDG&E';
  if (meter.type === 'pge') return 'PG&E';
  if (meter.type === 'sce') return 'SCE';
  return meter.type;
};

Meteor.isServer && Meteor.startup(async () => {
  await ConsumptionMeters.createIndexAsync({ orgId: 1 });
  await ConsumptionMeters.createIndexAsync({ name: 1 });
  await ConsumptionMeters.createIndexAsync({ identifier: 1 });
  await ConsumptionMeters.createIndexAsync({ accountNumber: 1 });
  await ConsumptionMeters.createIndexAsync({ type: 1 });
});